import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Platform, Pressable, Text } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { subscribeForegroundMessages } from '../services/foregroundMessaging';

type Props = {
  /** Bu rota aktifken toast gösterilmez (ör. Chat). */
  currentRouteName?: string;
};

export function ForegroundFcmBanner({ currentRouteName }: Props) {
  const insets = useSafeAreaInsets();
  const routeNameRef = useRef(currentRouteName);
  routeNameRef.current = currentRouteName;
  const [visible, setVisible] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-24)).current;

  const hide = useCallback(() => {
    Animated.parallel([
      Animated.timing(opacity, { toValue: 0, duration: 220, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: -24, duration: 220, useNativeDriver: true }),
    ]).start(() => setVisible(false));
  }, [opacity, translateY]);

  useEffect(() => {
    const unsub = subscribeForegroundMessages(payload => {
      if (routeNameRef.current === 'Chat') {
        return;
      }
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
      }
      setTitle(payload.title?.trim() || 'Yeni mesaj');
      setBody(payload.body?.trim() || 'Yeni bir mesajınız var');
      setVisible(true);
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 280, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, useNativeDriver: true, friction: 9 }),
      ]).start();
      hideTimer.current = setTimeout(() => {
        hide();
      }, 4500);
    });
    return () => {
      unsub();
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
      }
    };
  }, [hide, opacity, translateY]);

  if (!visible) {
    return null;
  }

  const topPad = Platform.OS === 'web' ? 12 : insets.top + 8;

  return (
    <Animated.View
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        left: 16,
        right: 16,
        top: topPad,
        zIndex: 9999,
        opacity,
        transform: [{ translateY }],
      }}>
      <Pressable
        onPress={hide}
        style={{
          borderRadius: 14,
          backgroundColor: '#ffffff',
          borderWidth: 1,
          borderColor: '#e4e4e7',
          paddingVertical: 12,
          paddingHorizontal: 14,
        }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: '#18181b' }} numberOfLines={1}>
          {title}
        </Text>
        {body ? (
          <Text style={{ marginTop: 4, fontSize: 13, color: '#71717a' }} numberOfLines={3}>
            {body}
          </Text>
        ) : null}
      </Pressable>
    </Animated.View>
  );
}
